// src/views/enseignant/ENAccueil.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useEnseignantService } from "../../services/enseignantService";
import { useEnseignantDrawer } from "../../context/EnseignantDrawerContext";

const BADGE_POLL_MS = 30000;

// Style commun : la page prend TOUTE la largeur disponible
const FULL_WIDTH = {
  width: "100%",
  maxWidth: "100%",
  alignSelf: "stretch",
  flex: "1 1 auto",
  boxSizing: "border-box",
};

// ─── HELPERS ──────────────────────────────────────────────────────────────
function getGreeting(username) {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return { text: `Bonjour ${username},\npassez une excellente journée !`, icon: "☀️" };
  if (h >= 12 && h < 18) return { text: `Bon après-midi ${username},\nbonne suite de journée !`, icon: "⛅" };
  if (h >= 18 && h < 22) return { text: `Bonsoir ${username},\nbonne fin de journée !`, icon: "🌙" };
  return { text: `Bonne nuit ${username},\nà demain pour vos classes !`, icon: "😴" };
}

// ─── SKELETON LOADER ──────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{
      ...FULL_WIDTH,
      width: "calc(100% + 32px)",
      maxWidth: "none",
      margin: "0 -16px",
    }}>
      <div style={{
        height: 270, borderRadius: "0 0 50px 50px",
        background: "linear-gradient(135deg, var(--pr-bg), var(--pr-lt))",
        animation: "skPulse 1.2s ease-in-out infinite alternate",
      }} />
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 16,
        marginTop: 16,
      }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            height: 64, borderRadius: 12, background: "var(--b2)",
            animation: "skPulse 1.2s ease-in-out infinite alternate",
            animationDelay: `${i * 0.15}s`,
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── CARTE CLASSE ──────────────────────────────────────────────────────
function ClasseCard({ classe, navigate, slug, index }) {
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    navigate(`/${slug}/enseignant/faire-appel?classe=${classe.id}`);
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "14px 16px",
        borderRadius: 16,
        background: "#fff",
        cursor: "pointer",
        boxShadow: hovered ? "0 10px 28px rgba(27,79,128,0.14)" : "0 3px 14px rgba(0,0,0,0.08)",
        transform: hovered ? "translateY(-2px)" : "none",
        transition: "all 0.2s ease",
        animation: "fadeUp 0.35s ease both",
        animationDelay: `${index * 0.06}s`,
      }}
    >
      <div style={{
        width: 44, height: 44,
        borderRadius: "50%",
        background: "var(--pr-bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: 14,
        flexShrink: 0,
      }}>
        <i className="ti ti-school" style={{ color: "var(--pr)", fontSize: 20 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: 15, color: "var(--t)" }}>
          {classe.nom}
        </div>
        {classe.niveau && (
          <div style={{ fontSize: 12, color: "var(--m)", marginTop: 2 }}>{classe.niveau}</div>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); handleClick(); }}
        style={{
          border: "1.5px solid var(--pr)",
          background: "none",
          color: "var(--pr)",
          borderRadius: 50,
          padding: "7px 14px",
          cursor: "pointer",
          fontFamily: "Inter, sans-serif",
          fontSize: 12,
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
          transition: "all 0.18s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--pr)";
          e.currentTarget.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "none";
          e.currentTarget.style.color = "var(--pr)";
        }}
      >
        <i className="ti ti-user-check" style={{ fontSize: 13 }} />
        <span>Faire l'appel</span>
      </button>
    </div>
  );
}

// ─── HERO SECTION ──────────────────────────────────────────────────────────
function HeroSection({ username, badges, navigate, slug }) {
  const { openDrawer } = useEnseignantDrawer();
  const { text, icon } = getGreeting(username);
  const lines = text.split("\n");

  return (
    <div style={{
      width: "100%",
      height: 220,
      flexShrink: 0,
      background: "linear-gradient(135deg, var(--pr-dk) 0%, var(--pr) 100%)",
      borderRadius: "0 0 50px 50px",
      boxShadow: "0 6px 24px rgba(0,0,0,0.22)",
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute",
        left: "20%",
        right: "20%",
        bottom: 0,
        height: 3,
        background: "linear-gradient(90deg, transparent, var(--or), transparent)",
      }} />

      <button
        onClick={openDrawer}
        style={{
          position: "absolute",
          top: 20,
          left: 16,
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: 12,
          width: 42,
          height: 42,
          cursor: "pointer",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className="ti ti-menu-2" style={{ color: "#fff", fontSize: 22 }} />
      </button>

      <div style={{ position: "absolute", top: 20, right: 16, display: "flex", gap: 8, zIndex: 10 }}>
        <button
          onClick={() => navigate(`/${slug}/enseignant/historique`)}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "50%",
            width: 42,
            height: 42,
            cursor: "pointer",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="ti ti-bell" style={{ color: "#fff", fontSize: 20 }} />
          {badges.notifications > 0 && (
            <div style={{
              position: "absolute",
              top: -4,
              right: -4,
              background: "var(--rd)",
              borderRadius: "50%",
              minWidth: 18,
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 2px",
            }}>{badges.notifications > 99 ? "99+" : badges.notifications}</div>
          )}
        </button>
        <button
          onClick={() => navigate(`/${slug}/enseignant/profil`)}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "50%",
            width: 42,
            height: 42,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="ti ti-user-circle" style={{ color: "#fff", fontSize: 22 }} />
        </button>
      </div>

      <div style={{
        position: "absolute",
        bottom: 28,
        left: 24,
        right: 24,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 28 }}>{icon}</span>
          <div style={{ textAlign: "center" }}>
            {lines.map((l, i) => (
              <div key={i} style={{
                fontFamily: "Poppins, sans-serif",
                color: "#fff",
                fontSize: i === 0 ? 18 : 14,
                fontWeight: i === 0 ? 700 : 400,
                textShadow: "0 2px 6px rgba(0,0,0,0.3)",
              }}>{l}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────
export default function ENAccueil({ slug }) {
  const navigate = useNavigate();
  const { getMesClasses, getMonProfil, getNotificationsNonLues } = useEnseignantService();

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState("Enseignant");
  const [badges, setBadges] = useState({ notifications: 0 });

  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT DES DONNÉES ─────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!slug) {
      setError("Identifiant établissement manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [classesRes, userRes] = await Promise.all([
        getMesClasses(slug),
        getMonProfil(slug).catch(() => ({ username: "Enseignant" })),
      ]);
      setClasses(classesRes || []);
      setUsername(userRes?.username || "Enseignant");
    } catch (err) {
      setError("Impossible de récupérer vos classes.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadData();
  }, [loadData]);

  // ─── CHARGEMENT DES BADGES ──────────────────────────────────────────
  const fetchBadges = useCallback(async () => {
    if (!slug || !getNotificationsNonLues) return;

    try {
      const notifsRes = await getNotificationsNonLues(slug).catch(() => ({ count: 0 }));
      setBadges({ notifications: notifsRes?.count ?? 0 });
    } catch (error) {
      setBadges({ notifications: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');

        html, body, #root { margin: 0; padding: 0; width: 100%; }

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }

        @keyframes skPulse {
          from { opacity: 0.55; }
          to { opacity: 1; }
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ─── Loader intégré dans la zone de contenu ─── */
        .en-accueil-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 14px;
        }

        .en-accueil-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: en-spin 0.8s linear infinite;
        }

        @keyframes en-spin { to { transform: rotate(360deg); } }

        .en-accueil-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        /* ─── Carte d'erreur intégrée ─── */
        .en-accueil-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }

        .en-accueil-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }

        .en-accueil-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .en-accueil-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 12px;
        }

        .en-accueil-error .actions-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .en-accueil-error .btn {
          border: none;
          border-radius: 30px;
          padding: 10px 22px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .en-accueil-error .btn:hover { opacity: 0.9; }

        .en-accueil-error .btn.btn-secondary {
          background: var(--b2);
          color: var(--t);
        }
      `}</style>

      <div style={{
        ...FULL_WIDTH,
        width: "calc(100% + 32px)",
        maxWidth: "none",
        margin: "0 -16px",
        background: "var(--s2)",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Hero TOUJOURS affiché */}
        <HeroSection
          username={username}
          badges={badges}
          navigate={navigate}
          slug={slug}
        />

        <div style={{ width: "100%", padding: 16, flex: 1 }}>
          {loading ? (
            /* ── ÉTAT CHARGEMENT ── */
            <div className="en-accueil-loading">
              <div className="spinner"></div>
              <span>Chargement de vos classes…</span>
            </div>
          ) : error ? (
            /* ── ÉTAT ERREUR ── */
            <div className="en-accueil-error">
              <i className="ti ti-alert-circle alert-icon" />
              <h2>Erreur de chargement</h2>
              <p>{error}</p>
              <div className="actions-row">
                <button
                  className="btn"
                  onClick={() => {
                    hasFetchedRef.current = false;
                    loadData();
                  }}
                >
                  <i className="ti ti-refresh" />
                  Réessayer
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/${slug}/enseignant/accueil`)}
                >
                  <i className="ti ti-home" />
                  Accueil
                </button>
              </div>
            </div>
          ) : classes.length === 0 ? (
            /* ── ÉTAT VIDE ── */
            <div className="card" style={{
              textAlign: "center",
              padding: 40,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              background: "#fff",
              borderRadius: 16,
            }}>
              <span style={{ fontSize: 44 }}>🏫</span>
              <p style={{ color: "var(--m)", fontSize: 16 }}>Aucune classe assignée.</p>
              <p style={{ fontSize: 13, color: "var(--h)" }}>
                Contactez l'administrateur pour vous assigner des classes.
              </p>
            </div>
          ) : (
            /* ── LISTE DES CLASSES ── */
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
              {classes.map((classe, i) => (
                <ClasseCard
                  key={classe.id}
                  classe={classe}
                  navigate={navigate}
                  slug={slug}
                  index={i}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}